import React from 'react';
import { Flex, Grid, Typography, theme } from 'antd';
import { GlassCard } from '../../../../shared/components';
import { RightCircleOutlined } from '@ant-design/icons';
import { useTranslate } from '@refinedev/core';

const { Title } = Typography;

interface FieldData {
    name: string;
    value: string;
    label: string;
}

interface OrderCharacteristicsProps {
    formValues: FieldData[];
}

type FieldGroupKey = 'characteristics' | 'readingMethod' | 'identification';

const FIELD_GROUP_GRID_CLASS: Record<FieldGroupKey, string> = {
    characteristics: 'grid-cols-2 lg:grid-cols-4',
    readingMethod: 'grid-cols-2 lg:grid-cols-3',
    identification: 'grid-cols-2',
};

const CharacteristicField: React.FC<{
    fieldData: FieldData;
    compact?: boolean;
}> = ({ fieldData, compact = false }) => {
    const { token } = theme.useToken();
    const labelClass = compact ? 'text-xs' : 'text-sm';

    return (
        <div className="min-w-0">
            <Flex align="flex-start" gap={8}>
                <span
                    className="inline-flex h-[22px] shrink-0 items-center leading-none"
                    style={{ color: token.colorPrimary }}
                >
                    <RightCircleOutlined />
                </span>
                <div className="min-w-0 flex-1">
                    <div
                        className="text-sm font-semibold leading-[22px] break-all"
                        style={{ color: token.colorText }}
                    >
                        {fieldData.value}
                    </div>
                    <div className={labelClass} style={{ color: token.colorTextSecondary }}>
                        {fieldData.label}
                    </div>
                </div>
            </Flex>
        </div>
    );
};

const CardTitle = ({ title, compact = false }: { title: string; compact?: boolean }) => {
    const { token } = theme.useToken();
    return (
        <Flex justify="left" align="center" className={compact ? "mb-2" : "mb-3"}>
            <div className={`text-center px-2 font-semibold ${compact ? "text-sm" : "text-base"}`} style={{ color: token.colorText }}>
                {title}
            </div>
        </Flex>
    );
};

export const OrderCharacteristics: React.FC<OrderCharacteristicsProps> = ({ formValues }) => {
    const _t = useTranslate();
    const { token } = theme.useToken();
    const breakpoint = Grid.useBreakpoint();
    const isMobile = typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg;

    const field = (name: string): FieldData | undefined => {
        return formValues.find((field: FieldData) => field.name === name);
    };

    const characteristicsFields = ['make', 'power', 'mileage', 'model', 'gearbox', 'engine', 'year'];
    const identificationFields = ['immatriculation', 'vin'];
    const readingMethodFields = ['calculator', 'prepared', 'method', 'technique', 'masterslave', 'hw', 'sw'];

    const renderFieldGroup = (fields: string[], title: string, groupKey: FieldGroupKey) => {
        const items = fields.filter(name => !!field(name)?.value);
        const gridClass = FIELD_GROUP_GRID_CLASS[groupKey];

        return (
            <GlassCard className="shadow-lg">
                <CardTitle title={title} compact={isMobile} />
                <div
                    className={`grid ${gridClass} ${isMobile ? 'gap-x-3 gap-y-3' : 'gap-x-6 gap-y-4'}`}
                >
                    {items.map((name) => {
                        const fieldData = field(name)!;
                        return (
                            <CharacteristicField
                                key={name}
                                fieldData={fieldData}
                                compact={isMobile}
                            />
                        );
                    })}
                </div>
            </GlassCard>
        );
    };

    return (
        <>
            <Title
                level={isMobile ? 5 : 4}
                className={isMobile ? "mt-3 mb-2" : "mt-8"}
                style={{ color: token.colorText }}
            >
                <RightCircleOutlined className="mr-2" style={{ color: token.colorPrimary }} />
                {_t("orders.fields.caracteristics")}
            </Title>

            <div className={isMobile ? "mt-0" : "mt-2"}>
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${isMobile ? "gap-3" : "gap-6"}`}>
                    {renderFieldGroup(characteristicsFields, _t("orders.fields.caracteristics"), 'characteristics')}
                    {renderFieldGroup(readingMethodFields, _t("orders.fields.readingMethod"), 'readingMethod')}
                    {renderFieldGroup(identificationFields, _t("orders.fields.identification"), 'identification')}
                </div>
            </div>
        </>
    );
};
